import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import _ from 'lodash';
import p5 from 'p5';
import {IconButton, Stack, useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const ImageDisplayContext = createContext({});

export const useImageDisplay = ()=>useContext(ImageDisplayContext);

const PADDING = 20;

const runP5 = (element, imageURLs, index, close)=>{
    let images = {};
    let vw, vh;
    let offsetX = 0;
    let dragging, dragStartX, dragDelta;
    let pressedOutOfBounds;
    let zoomStart = [], zoomAnchor = [], zoomScale = 1;
    let setup = p=>{
        p.createCanvas(p.windowWidth, p.windowHeight).parent(element);
        imageURLs.forEach((url, i)=>p.loadImage(url, img=>{ images[i] = img; }));
        vw = p.width*0.01;
        vh = p.height*0.01;
    };
    let next = ()=>{
        index++;
        if (index>=imageURLs.length)
            index = 0;
    };
    let prev = ()=>{
        index--;
        if (index<0)
            index = imageURLs.length-1;
    };
    let keyPressed = p=>{
        switch (p.key) {
            case 'Escape': close(); break;
            case 'ArrowLeft': prev(); break;
            case 'ArrowRight': next(); break;
        }
    };
    let imageRect = (p, img)=>{
        let w, h;
        if (img.height>img.width) {
            let aspect = img.width/img.height;
            h = p.height-PADDING*2;
            w = h*aspect;
            if (w>p.width) {
                aspect = img.height/img.width;
                w = p.width-PADDING*2;
                h = w*aspect;
            }
        } else {
            let aspect = img.height/img.width;
            w = p.width-PADDING*2;
            h = w*aspect;
            if (h>p.height) {
                aspect = img.width/img.height;
                h = p.height-PADDING*2;
                w = h*aspect;
            }
        }
        w*=zoomScale;
        h*=zoomScale;
        return {x: p.width/2-w/2+offsetX, y: p.height/2-h/2, w, h};
    };
    let inBounds = (rect, x, y)=>x>=rect.x&&x<rect.x+rect.w&&y>=rect.y&&y<rect.y+rect.h;
    let startMove = (startX, touchX)=>{
        dragging = true;
        dragStartX = startX;
        dragDelta = touchX-dragStartX;
    };
    let dragMove = touchX=>{
        if (!dragging)
            return;
        offsetX = touchX-dragStartX-dragDelta;
    };
    let endMove = p=>{
        if (offsetX>p.width*0.4)
            next();
        else if (offsetX<-p.width*0.4)
            prev();
        dragging = false;
        dragStartX = 0;
        dragDelta = 0;
        offsetX = 0;
    };
    let mousePressed = p=>{
        if (images[index]) {
            let rect = imageRect(p, images[index]);
            if (inBounds(rect, p.mouseX, p.mouseY))
                return void startMove(rect.x, p.mouseX);
        }
        pressedOutOfBounds = true;
    };
    let mouseDragged = p=>dragMove(p.mouseX);
    let mouseReleased = p=>{
        endMove(p);
        if (pressedOutOfBounds&&images[index]) {
            let rect = imageRect(p, images[index]);
            if (!inBounds(rect, p.mouseX, p.mouseY))
                close();
        }
        pressedOutOfBounds = false;
    }
    let touchStarted = (p, ev)=>{
        ev.stopPropagation();
        ev.preventDefault();
        if (p.touches.length==1) {
            if (images[index]) {
                let rect = imageRect(p, images[index]);
                if (inBounds(rect, p.touches[0].x, p.touches[0].y))
                    return void startMove(rect.x, p.touches[0].x);
            }
            close(); // out of bounds
        } else {
            zoomStart = [p.touches[0], p.touches[1]];
            zoomAnchor = [p.touches[0], p.touches[1]];
        }
    };
    let touchMoved = (p, ev)=>{
        ev.stopPropagation();
        ev.preventDefault();
        if (p.touches.length==1)
            return void dragMove(p.touches[0].x);
        zoomAnchor[0] = p.touches[0];
        zoomAnchor[1] = p.touches[1];
        let startDist = p.dist(zoomStart[0].x, zoomStart[0].y, zoomStart[1].x, zoomStart[1].y);
        let anchorDist = p.dist(zoomAnchor[0].x, zoomAnchor[0].y, zoomAnchor[1].x, zoomAnchor[1].y);
        zoomScale = Math.max(1, 1+((anchorDist-startDist)*0.01));
    };
    let touchEnded = p=>{
        endMove(p);
        zoomScale = 1;
        zoomStart = zoomAnchor = [];
    }
    let windowResized = p=>{
        p.resizeCanvas(p.windowWidth, p.windowHeight);
        vw = p.width*0.01;
        vh = p.height*0.01;
    };
    let draw = p=>{
        p.clear();
        p.push();
        if (images[index]) {
            let {x, y, w, h} = imageRect(p, images[index]);
            p.image(images[index], x, y, w, h);
        }
        p.pop();
    };
    let p5Instance = new p5(p=>{
        p.setup = ()=>setup(p);
        p.keyPressed = ()=>keyPressed(p);
        p.mousePressed = ()=>mousePressed(p);
        p.mouseDragged = ()=>mouseDragged(p);
        p.mouseReleased = ()=>mouseReleased(p);
        p.touchStarted = ev=>touchStarted(p, ev);
        p.touchMoved = ev=>touchMoved(p, ev);
        p.touchEnded = ()=>touchEnded(p);
        p.windowResized = ()=>windowResized(p);
        p.draw = ()=>draw(p);
    });
    return ()=>p5Instance.remove();
};

const ImageContainer = props=>{
    let {images, index, close} = props;
    let theme = useTheme();
    let [p5Node, setP5Node] = useState();
    useEffect(()=>{
        if (!p5Node)
            return;
        return runP5(p5Node, images, index, close);
    }, [p5Node, images, index, close]);
    return createPortal(<Stack position="fixed"
      justifyContent="center" alignItems="center"
      sx={{
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}>
      <div ref={setP5Node} style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
      }}>
      </div>
      <IconButton onClick={close} sx={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          color: theme.palette.primary.contrastText,
      }}>
        <CloseIcon/>
      </IconButton>
    </Stack>, document.body);
};

export const ImageDisplayProvider = props=>{
    let {children} = props;
    let [images, setImages] = useState([]);
    let [index, setIndex] = useState(0);
    let close = useCallback(()=>{
        setImages([]);
        setIndex(0);
    }, []);
    let displayImage = useCallback(data=>{
        if (_.isString(data)) {
            setImages([data]);
            setIndex(0);
            return;
        }
        setImages(data.images);
        setIndex(data.index||0);
    }, []);
    return <ImageDisplayContext.Provider value={displayImage}>
      {children}
      {!!images?.length&&<ImageContainer
        images={images}
        index={index}
        close={close}
      />}
    </ImageDisplayContext.Provider>;
};
